'use client'

import { ReactNode, useContext } from 'react'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'

import SortContext from './SortContext'

type Props = {
  testId?: string
  className?: string
  children: ReactNode
  onDragStart?(index: number): void
  onDragOver?(oldIndex: number, newIndex: number | undefined): void
  onSortUpdate(oldIndex: number, newIndex: number): void
}

const Content = ({ testId, className, children, onDragStart, onDragOver, onSortUpdate }: Props) => {
  const { items, indexes } = useContext(SortContext)
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  function handleDragStart({ active }: DragStartEvent) {
    onDragStart?.(indexes[active.id]!)
  }

  function handleDragOver({ active, over }: DragOverEvent) {
    onDragOver?.(indexes[active.id]!, over ? indexes[over.id] : undefined)
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over) return

    onSortUpdate(indexes[active.id]!, indexes[over.id]!)
  }

  return (
    <DndContext
      accessibility={{
        // Disabling accessibility because it's too quirky and it conflicts with MediaSelect announcements
        screenReaderInstructions: { draggable: '' },
      }}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      autoScroll={false}
    >
      <SortableContext items={items}>
        <div className={className} data-testid={testId}>
          {children}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export default Content
